export interface AppDefinition {
  id: string;
  name: string;
  displayName: string;
  shortDisplayName?: string; // NEW: Optional short name for mobile
  route: string;
  category: 'productivity' | 'creative' | 'business' | 'automation' | 'ai' | 'education';
  icon: string;
  description?: string;
  status: 'active' | 'beta' | 'coming_soon';
  // For usage tracking
  usageLabel: string;
  // For routing and navigation
  isPublic?: boolean;
  // For permissions
  requiresSubscription?: boolean;
}

export const APP_REGISTRY: Record<string, AppDefinition> = {
  // Productivity Apps
  'task-manager': {
    id: 'task-manager',
    name: 'task-manager',
    displayName: 'Tasks',
    shortDisplayName: 'Tasks',
    route: '/task-manager',
    category: 'productivity',
    icon: 'ListTodo',
    description: 'Organize and track your strategic initiatives with powerful task management tools',
    status: 'active',
    usageLabel: 'Task Manager',
  },

  // Creative Apps
  'content-generator': {
    id: 'content-generator',
    name: 'content-generator',
    displayName: 'Projects',
    shortDisplayName: 'Projects',
    route: '/project-manager',
    category: 'creative',
    icon: 'NotebookTabs',
    description: 'View all projects, organize tasks, and create content',
    status: 'active',
    usageLabel: 'Projects',
  },

  'image-generator': {
    id: 'image-generator',
    name: 'image-generator',
    displayName: 'Images',
    shortDisplayName: 'Images',
    route: '/private-image-generator',
    category: 'creative',
    icon: 'Images',
    description: 'Generate and manage your private AI images.',
    status: 'active',
    usageLabel: 'Image Generator',
  },

  // 'video-generator': {
  //   id: 'video-generator',
  //   name: 'video-generator',
  //   displayName: 'Videos',
  //   shortDisplayName: 'Videos',
  //   route: '/video-generator',
  //   category: 'creative',
  //   icon: 'Clapperboard',
  //   description: 'Generate AI-powered videos with audio using advanced video generation models.',
  //   status: 'active',
  //   usageLabel: 'Video Generator',
  // },

  'research-generator': {
    id: 'research-generator',
    name: 'research-generator',
    displayName: 'Research',
    shortDisplayName: 'Research',
    route: '/private-research-generator',
    category: 'education',
    icon: 'Microscope',
    description: 'Generate and manage your private research library with comprehensive AI-powered research tools.',
    status: 'active',
    usageLabel: 'Private Research Generator',
  },

  'text-generator': {
    id: 'text-generator',
    name: 'text-generator',
    displayName: 'Documents',
    shortDisplayName: 'Documents',
    route: '/private-text-generator',
    category: 'creative',
    icon: 'FileText',
    description: 'Create and manage your private document library with AI-powered writing tools.',
    status: 'active',
    usageLabel: 'Private Document Generator',
  },

  // AI Apps
  'ai-agents': {
    id: 'ai-agents',
    name: 'ai-agents', 
    displayName: 'AI Agents',
    route: '/account/ai-agents',
    category: 'ai',
    icon: 'Bot',
    description: 'Interact with specialized AI assistants',
    status: 'coming_soon',
    usageLabel: 'AI Agents',
  },

  // Special/System Apps
  'app-library': {
    id: 'app-library',
    name: 'app-library',
    displayName: 'App Library',
    route: '/app-library',
    category: 'automation',
    icon: 'Grid3X3',
    description: 'Browse and access all applications',
    status: 'coming_soon',
    usageLabel: 'App Library',
  },

  'account': {
    id: 'account',
    name: 'account',
    displayName: 'Account',
    route: '/account',
    category: 'automation',
    icon: 'User',
    description: 'Manage your account settings',
    status: 'coming_soon', 
    usageLabel: 'Account',
  },

  'storage': {
    id: 'storage',
    name: 'storage',
    displayName: 'Storage',
    route: '/storage',
    category: 'automation',
    icon: 'Database',
    description: 'Manage your stored content and files',
    status: 'coming_soon',
    usageLabel: 'Storage',
  },

  // Public Libraries
  'public-image-library': {
    id: 'public-image-library',
    name: 'public-image-library',
    displayName: 'Public Images',
    shortDisplayName: 'Public Images',
    route: '/public-image-library',
    category: 'creative',
    icon: 'ImageIcon',
    description: 'Browse and use images from the public image collection',
    status: 'active',
    usageLabel: 'Public Image Library',
    isPublic: true,
  },

  'public-research-library': {
    id: 'public-research-library',
    name: 'public-research-library',
    displayName: 'Public Research',
    shortDisplayName: 'Public Research',
    route: '/public-research-library',
    category: 'education',
    icon: 'Library',
    description: 'Access and explore the public research knowledge base',
    status: 'active',
    usageLabel: 'Public Research Library',
    isPublic: true,
  },
};

// Helper functions for app registry
export const getAppById = (id: string): AppDefinition | undefined => {
  return APP_REGISTRY[id];
};

export const getAppByRoute = (route: string): AppDefinition | undefined => {
  return Object.values(APP_REGISTRY).find(app => app.route === route);
};

export const getAppByName = (name: string): AppDefinition | undefined => {
  return Object.values(APP_REGISTRY).find(app => app.name === name);
};

export const getAppsByCategory = (category: AppDefinition['category']): AppDefinition[] => {
  return Object.values(APP_REGISTRY).filter(app => app.category === category);
};

export const getActiveApps = (): AppDefinition[] => {
  return Object.values(APP_REGISTRY).filter(app => app.status === 'active');
};

// Get usage label for AI usage tracking
export const getUsageLabelForRoute = (route: string): string => {
  const app = getAppByRoute(route);
  return app?.usageLabel || 'Unknown App';
};

// Get usage label by app ID
export const getUsageLabelById = (id: string): string => {
  const app = getAppById(id);
  return app?.usageLabel || 'Unknown App';
};

// Route-based app detection for automatic labeling
export const detectAppFromUrl = (url: string): string => {
  // Handle exact matches first
  const app = getAppByRoute(url);
  if (app) return app.usageLabel;
  
  // Handle route patterns for dynamic routes and sub-pages
  if (url.includes('/image-generator')) return 'Image Generator';
  if (url.includes('/video-generator')) return 'Video Generator';
  if (url.includes('/project-manager')) return 'Browse Projects';
  if (url.includes('/private-research-generator')) return 'Private Research Generator';
  if (url.includes('/research-generator')) return 'Research Generator';
  if (url.includes('/task-manager')) return 'Projects';
  if (url.includes('/app-library')) return 'App Library';
  if (url.includes('/account')) return 'Account';
  if (url.includes('/billing')) return 'Billing & Usage';
  if (url.includes('/storage')) return 'Storage';
  if (url.includes('/public-image-library')) return 'Public Image Library';
  if (url.includes('/public-research-library')) return 'Public Research Library';
  
  return 'Unknown App';
};

export const getAppDescription = (id: string): string => {
  const app = getAppById(id);
  return app?.description || 'No description available';
};

export default APP_REGISTRY; 